/**
 * @fileOverview This file contains the implementation of the Toolbar component for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region Imports
import { useAtom } from 'jotai';
import { useState } from 'react';
import {
  AppBar,
  Grid,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Switch,
  Badge,
  createTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faCartShopping,
} from '@fortawesome/pro-regular-svg-icons';

import Filter from './Filter';
import MoreInfoDialog from './MoreInfoDialog';
import CreateItemModal from './CreateItemModal';
import variables from '../../scss/_variables.scss';
import '../../scss/Components/Inventory/_toolbar.scss';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import { movementsArrayAtom } from '../../stores/movementsStore';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import MultipleMovementsModal from './MultipleMovementsModal';
import { ThemeProvider } from '@emotion/react';
//#endregion

const theme = createTheme({
  palette: {
    primary: {
      main: variables.redDabi,
    },
    secondary: {
      main: '#6c757d',
    },
  },
});

const InventoryToolbar = ({
  setFilters,
  filterInventoryData,
  setFilterInventoryData,
  refreshData,
}) => {
  //#region Use state variables
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openMultipleModal, setOpenMultipleModal] = useState(false);
  //#endregion

  const [movements] = useAtom(movementsArrayAtom);

  const { addString } = useTranslationHook();

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleChange = (event) => {
    setFilterInventoryData(event.target.checked);
  };

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position='static'
        sx={{ backgroundColor: 'transparent', boxShadow: 'none !important' }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Grid
            sx={{
              display: 'flex',
              width: '100%',
              gap: '2rem',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='h6'
              component='div'
              className='inventory-toolbar-title'
            >
              Almacén
            </Typography>

            <button
              className='add-button'
              onClick={() => setOpenCreateModal(true)}
            >
              {addString} <FontAwesomeIcon icon={faAdd} />
            </button>

            <CreateItemModal
              show={openCreateModal}
              onHide={() => setOpenCreateModal(false)}
              refreshData={refreshData}
            />

            <MultipleMovementsModal
              show={openMultipleModal}
              onHide={() => setOpenMultipleModal(false)}
              refreshData={refreshData}
            />

            <Filter setFilters={setFilters} />
            <Grid
              sx={{
                display: 'flex',
                width: '80%',
                gap: '2rem',
                alignItems: 'center',
              }}
            >
              <Grid
                sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                aria-owns={open ? 'agenda-popover' : undefined}
                aria-haspopup='true'
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className='inventory-more-info-icon'
                />
                <Typography className='inventory-more-info-label'>
                  Más información
                </Typography>
              </Grid>
              <MoreInfoDialog
                open={open}
                anchor={anchorEl}
                close={handlePopoverClose}
              />
            </Grid>
          </Grid>
          <Box
            width={'20%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'end'}
          >
            <Typography className='inventory-toolbar-switch-label'>
              Mostrar tipos de equipos sin productos
            </Typography>
            <Switch
              checked={filterInventoryData}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <IconButton
              color='black'
              aria-label='cart'
              onClick={() => setOpenMultipleModal(true)}
            >
              <Badge badgeContent={movements.length} color='primary'>
                <FontAwesomeIcon icon={faCartShopping} />
              </Badge>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
};

export default InventoryToolbar;
